import { Reason } from './Reason.js';
import { ActionButton, ActionInput } from '../parts/index.js';
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import type { Store } from '../api.js';
import { admin, type CompetitionRow, type MapCounts, type NearestOrganisation, type OrgMapRow, type UnmatchedKey, type UnmatchedView } from './api.js';
import { n } from './format.js';
import { applyDecision, carriesDecision } from './snowflake-decide.js';
import { Problem, said, useLoad } from './console-shared.js';
import { Leagues } from './Leagues.js';
import { RightsHolders } from './RightsHolders.js';

function countsLine(c: MapCounts, noun: string): string {
  return `${n(c.linked)} of ${n(c.inStore)} ${noun} linked · ${n(c.candidates)} proposed and waiting for a person · ${n(c.rejected)} rejected`;
}

function stateCell(r: { state: string; confirmedBy: string | null; confirmedAt: string | null; rejectedAt: string | null }) {
  if (r.state === 'confirmed') return <>confirmed by {r.confirmedBy ?? 'a person'}{r.confirmedAt ? <><br /><span className="dim">{r.confirmedAt.slice(0, 10)}</span></> : null}</>;
  if (r.state === 'rejected') return <>rejected{r.rejectedAt ? <><br /><span className="dim">{r.rejectedAt.slice(0, 10)}</span></> : null}</>;
  return <span className="dim">proposed</span>;
}

const COBRAIN_BASIS: Record<string, string> = { name: 'the same name', alias: 'a name the company is known by', judgement: "the master's judgement" };

/** A company row's state: a live link Cobrain made says so and why (D227, B527); an undone one says Cobrain had linked it. */
function orgStateCell(r: OrgMapRow) {
  if (r.state === 'confirmed' && r.cobrainLinkedAt && !r.confirmedBy) {
    return <>linked by Cobrain{r.cobrainBasis ? <>, on {COBRAIN_BASIS[r.cobrainBasis] ?? r.cobrainBasis}</> : null}
      {r.cobrainReason ? <div className="dim small">{r.cobrainReason}</div> : null}
      {r.confirmedAt ? <span className="dim">{r.confirmedAt.slice(0, 10)}</span> : null}</>;
  }
  if (r.state === 'rejected' && r.cobrainLinkedAt) {
    return <>rejected{r.rejectedAt ? <><br /><span className="dim">{r.rejectedAt.slice(0, 10)}</span></> : null}<div className="dim small">Cobrain had linked it</div></>;
  }
  return stateCell(r);
}

/** A figure on the confirmation screen, or a visible blank when the copy holds nothing for that key. */
function evidence(n: number | null): ReactNode {
  if (n === null || n === undefined) return <span className="dim">—</span>;
  return Math.round(n).toLocaleString('en-GB');
}

/**
 * A figure with the company's OTHER rows beside it (module 04 DESIGN Open 67). A company that is both
 * a OneTrade group and a OneVideo operator is two rows, and each holds half the evidence: without this the
 * group row showed a blank under views and read as "this company has no OneVideo usage" when it meant
 * "not on this row". A blank now means one thing: the copy holds nothing for the company.
 */
function evidenceWithOthers(own: number | null, other: number | null, severalOthers: boolean, beside?: ReactNode): ReactNode {
  const f = (x: number) => Math.round(x).toLocaleString('en-GB');
  const where = severalOthers ? 'on its other rows' : 'on its other row';
  if (own === null || own === undefined) {
    if (other === null || other === undefined) return <span className="dim">—</span>;
    return <span className="dim">{f(other)}<div className="small">{where}</div></span>;
  }
  return <>{f(own)}{beside}{other !== null && other !== undefined ? <div className="dim small">+ {f(other)} {where}</div> : null}</>;
}

/** Whether a row's company has more than one OTHER standing row: companyRows counts this row too, unless it was rejected. */
const severalOthers = (r: OrgMapRow): boolean => r.companyRows - (r.state === 'rejected' ? 0 : 1) > 1;

const KEY_KIND: Record<UnmatchedKey['kind'], string> = { group: 'OneTrade group', customer: 'OneVideo operator' };

/**
 * The Snowflake customers with usage that no row reaches (item 59 of module 04 DESIGN's Open list):
 * the name matched no company, or a person rejected the one it matched. Nothing of theirs can be
 * confirmed, so none of it reaches a company page, until a person says which company each is. Picking
 * one writes a PROPOSAL, which then sits in the list with its figures and is confirmed like any other.
 */
function Unreached({ canSave, store, unmatched, busy, pick }: {
  canSave: boolean; store: Store; unmatched: UnmatchedView; busy: boolean; pick: (k: UnmatchedKey, o: NearestOrganisation) => void;
}) {
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<NearestOrganisation[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  if (unmatched.keys === 0) return null;
  const id = (k: UnmatchedKey) => `${k.kind}:${k.key}`;
  // A choice closes the search it came from. Found on the page: the list reloads under the open
  // search, and the result still sitting there was a button for the company just rejected.
  const choose = (k: UnmatchedKey, o: NearestOrganisation) => { setSearching(null); setFound(null); setTerm(''); setSearchError(null); pick(k, o); };
  const find = (e: FormEvent) => {
    e.preventDefault(); setSearchError(null); setFound(null);
    if (term.trim().length < 2) { setSearchError('Type at least two letters of the company\'s name.'); return; }
    admin.searchCompanies(store.tenantId, term.trim()).then((r) => setFound(r.organisations)).catch((x: unknown) => setSearchError(said(x)));
  };
  const total = [
    unmatched.watchedHours > 0 ? `${n(Math.round(unmatched.watchedHours))} watched hours` : null,
    unmatched.oneVideoViews > 0 ? `${n(Math.round(unmatched.oneVideoViews))} OneVideo views` : null,
  ].filter(Boolean).join(' and ');
  return (
    <details className="admin-unreached" open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary>
        <strong>{n(unmatched.keys)} Snowflake customers reach no company</strong>
        <span> · {total} in the last twelve months that the list below cannot put on any company page. Say which company each is.</span>
      </summary>
      <p className="admin-note">
        Their names matched no company here, or a person rejected the match. Choosing a company writes a proposal: it joins
        the list below with its figures and is confirmed or rejected there like any other. A company that is not in Cobrain
        at all cannot be chosen here.
      </p>
      <div className="admin-scroll">
        <table className="admin">
          <thead><tr><th>Snowflake name</th><th className="num">Watched hours</th><th className="num">Seats</th><th className="num">OneVideo views</th><th>Which company is it?</th></tr></thead>
          <tbody>
            {unmatched.rows.map((k) => (
              <tr key={id(k)}>
                <td><strong>{k.name ?? 'no name in Snowflake'}</strong>{k.isTrial ? <span className="dim"> · trial</span> : null}
                  <div className="dim small">{KEY_KIND[k.kind]} {k.key}{k.rejectedOrganisation ? ` · a person said it is not ${k.rejectedOrganisation}` : ''}</div></td>
                <td className="num">{evidence(k.watchedHours)}</td>
                <td className="num">{evidence(k.licencesCount)}</td>
                <td className="num">{evidence(k.oneVideoViews)}</td>
                <td>
                  <div className="admin-actions">
                    {k.nearest.map((o) => (
                      <ActionButton key={o.organisationId} className="small" type="button" disabled={busy || !canSave} onClick={() => choose(k, o)}>{o.name}</ActionButton>
                    ))}
                    {k.nearest.length === 0 ? <span className="dim small">no company of a similar name</span> : null}
                    <ActionButton reading className="small quiet" type="button" disabled={busy}
                      onClick={() => { setSearching(searching === id(k) ? null : id(k)); setTerm(''); setFound(null); setSearchError(null); }}>
                      {searching === id(k) ? 'Close' : 'Another company…'}
                    </ActionButton>
                  </div>
                  {searching === id(k) ? (
                    <form className="admin-find" onSubmit={find}>
                      <ActionInput reading aria-label={`company for ${k.name ?? k.key}`} placeholder="company name" value={term} autoFocus onChange={(e) => setTerm(e.target.value)} />
                      <ActionButton reading className="small" type="submit">Find</ActionButton>
                      {searchError ? <span className="admin-says parked">{searchError}</span> : null}
                      {found !== null && found.length === 0 ? <span className="dim small">No company of that name in Cobrain.</span> : null}
                      {found && found.length > 0 ? (
                        <div className="admin-actions">
                          {found.map((o) => <ActionButton key={o.organisationId} className="small" type="button" disabled={busy || !canSave} onClick={() => choose(k, o)}>{o.name}</ActionButton>)}
                        </div>
                      ) : null}
                    </form>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

export function Snowflake({ store }: { store: Store }) {
  const load = useCallback(() => admin.snowflake(store.tenantId), [store.tenantId]);
  const { data, error, reload, amend } = useLoad(load);
  // The unreached keys are their own, dearer read (0.8 s on the live store): asked for on opening and
  // when they can have changed — a choice, or a company's proposal rejected — and never after a
  // confirmation, which is what a person presses two hundred times in a row.
  const loadUnmatched = useCallback(() => admin.unmatched(store.tenantId), [store.tenantId]);
  const { data: unreached, error: unreachedError, reload: reloadUnmatched } = useLoad(loadUnmatched);
  const [reason, setReason] = useState('');
  const canSave = reason.trim().length > 0;
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const act = (kind: 'organisation' | 'competition', id: string, verb: 'confirm' | 'reject') => {
    if (!canSave || busy !== null) return;
    setBusy(id); setActionError(null); setPicked(null);
    admin.decide(store.tenantId, kind, id, verb, reason.trim())
      .then((res) => {
        setReason('');
        // A confirmation changes its own row and nothing else, and the answer says what was written,
        // so it is put on the row here and the list is not read again: that read is about half a
        // second as the app role on the live store, after each of up to two hundred presses (B395).
        // A rejected COMPANY row is the exception, and is read again as before: its figures stop
        // counting on its company's other rows, and its key comes back into the unreached offer.
        if (kind === 'organisation' && verb === 'reject') { reload(); reloadUnmatched(); return; }
        const d = res.decided;
        if (carriesDecision(d)) amend((view) => applyDecision(view, kind, d)); else reload();
      })
      .catch((e: unknown) => setActionError(said(e))).finally(() => setBusy(null));
  };
  const pick = (k: UnmatchedKey, o: NearestOrganisation) => {
    if (!canSave || busy !== null) return;
    setBusy(`${k.kind}:${k.key}`); setActionError(null); setPicked(null);
    admin.propose(store.tenantId, k.kind, k.key, o.organisationId, reason.trim())
      .then(() => { setReason(''); setPicked(`${k.name ?? k.key} is now proposed for ${o.name}. It is in the list below with its figures: confirm it there, or reject it to choose again.`); reload(); reloadUnmatched(); })
      .catch((e: unknown) => setActionError(said(e))).finally(() => setBusy(null));
  };
  // Cobrain's own link is undone by rejecting it: the row keeps who linked it and why (D227, B527).
  const undo = (id: string) => (
    <div className="admin-actions">
      <ActionButton className="small danger" type="button" disabled={busy !== null || !canSave} title="This account is not this company: undo Cobrain's link" onClick={() => act('organisation', id, 'reject')}>Reject</ActionButton>
    </div>
  );
  const buttons = (kind: 'organisation' | 'competition', id: string) => (
    <div className="admin-actions">
      <ActionButton className="small" type="button" disabled={busy !== null || !canSave} onClick={() => act(kind, id, 'confirm')}>Confirm</ActionButton>
      <ActionButton className="small danger" type="button" disabled={busy !== null || !canSave} onClick={() => act(kind, id, 'reject')}>Reject</ActionButton>
    </div>
  );
  return (
    <>
      <Problem message={error ?? actionError ?? unreachedError} />
      <div className="admin-head"><h2>Snowflake map</h2><span>which Snowflake customer each company is, and which competition each right is</span></div>
      <Reason value={reason} onChange={setReason} busy={busy !== null} />
      <p className="admin-note">
        Cobrain links each OneTrade group and OneVideo operator to its company itself when it is sure: by the same name, by a
        name the company is known by, or on the master&rsquo;s judgement, and each link says why. What it was not sure of is
        at the top, for a person to confirm or reject. Reject undoes any link Cobrain made; a rejection is kept, so Cobrain
        never makes that link again.
      </p>
      <p className="admin-note">
        Watched hours are the last twelve complete months. A company that is both a OneTrade group and a OneVideo operator
        has a row for each, side by side: a dimmed figure is on the company&rsquo;s other row, and a blank means the copy holds
        nothing for the company at all.
        <strong> No usage figure, rule or alert exists for a company until one of its rows here is linked.</strong>
      </p>
      {data === null && !error ? <p className="meta pad">Reading the map…</p> : null}
      {data ? (
        <>
          {picked ? <p className="admin-says ok" role="status">{picked}</p> : null}
          {unreached ? <Unreached canSave={canSave} store={store} unmatched={unreached.unmatched} busy={busy !== null} pick={pick} /> : null}
          <div className="admin-head"><h2>Companies</h2><span>{countsLine(data.organisations.counts, 'companies')}</span></div>
          <div className="admin-scroll">
            <table className="admin">
              <thead><tr><th>Company</th><th>Snowflake name</th><th>Matched by</th><th className="num">Watched hours</th><th className="num">Seats · active</th><th className="num">OneVideo views</th><th>State</th><th /></tr></thead>
              <tbody>
                {data.organisations.rows.map((r: OrgMapRow) => (
                  <tr key={r.id} className={r.state === 'candidate' ? undefined : 'decided'}>
                    <td><strong>{r.organisationName}</strong>{r.isTrial ? <span className="dim"> · trial</span> : null}
                      {r.companyRows > 1 ? <div className="dim small">one of this company&rsquo;s {r.companyRows} rows</div> : null}</td>
                    <td>{r.proposedName ?? <span className="dim">added by hand</span>}
                      <div className="dim small">{r.groupId ? `OneTrade group ${r.groupId}` : null}{r.groupId && r.customerId ? ' · ' : null}{r.customerId ? `OneVideo operator ${r.customerId}` : null}</div></td>
                    <td>{r.basis ?? <span className="dim">{r.cobrainBasis === 'judgement' ? 'the master’s judgement' : r.proposedName ? 'chosen by a person' : '—'}</span>}</td>
                    <td className="num">{evidenceWithOthers(r.watchedHours, r.otherWatchedHours, severalOthers(r))}{r.daysSinceLastActivity !== null && r.daysSinceLastActivity > 60
                      ? <div className="dim small">quiet {r.daysSinceLastActivity}d</div> : null}</td>
                    <td className="num">{evidenceWithOthers(r.licencesCount, r.otherLicencesCount, severalOthers(r),
                      r.activeUsers30d !== null ? <span className="dim"> · {r.activeUsers30d}</span> : null)}</td>
                    <td className="num">{evidenceWithOthers(r.oneVideoViews, r.otherOneVideoViews, severalOthers(r))}</td>
                    <td>{orgStateCell(r)}</td>
                    <td>{r.state === 'candidate' ? buttons('organisation', r.id) : r.state === 'confirmed' && r.cobrainLinkedAt && !r.confirmedBy ? undo(r.id) : null}</td>
                  </tr>
                ))}
                {data.organisations.rows.length === 0 ? <tr><td colSpan={8} className="dim">Nothing proposed yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <div className="admin-head"><h2>Rights</h2><span>{countsLine(data.competitions.counts, 'rights properties')}</span></div>
          <div className="admin-scroll">
            <table className="admin">
              <thead><tr><th>Right</th><th>Snowflake name</th><th>Matched by</th><th>Sport · competition · id</th><th>State</th><th /></tr></thead>
              <tbody>
                {data.competitions.rows.map((r: CompetitionRow) => (
                  <tr key={r.id} className={r.state === 'candidate' ? undefined : 'decided'}>
                    <td><strong>{r.propertyName}</strong></td>
                    <td>{r.proposedName ?? <span className="dim">added by hand</span>}</td>
                    <td>{r.basis ?? <span className="dim">—</span>}</td>
                    <td>{r.sport ?? '—'} · {r.competition ?? '—'} · {r.oneplatformId ?? '—'}</td>
                    <td>{stateCell(r)}</td>
                    <td>{r.state === 'candidate' ? buttons('competition', r.id) : null}</td>
                  </tr>
                ))}
                {data.competitions.rows.length === 0 ? <tr><td colSpan={6} className="dim">Nothing proposed yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <RightsHolders store={store} />
          <Leagues store={store} />
        </>
      ) : null}
    </>
  );
}

